import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import {
  type ApprovalRuleAmount,
  models as approvalModels,
} from 'src/core/modules/company/structure/approval-flows';
import { formatMoney } from 'src/core/utils/money';
import { TypeGuardError } from 'src/core/utils/switchGuard';

import {
  type ApprovalRuleExport,
  type ApprovalRangeExport,
  type ApprovalSchemeExport,
  type ApprovalApproverExport,
} from './approvalWorkflowExportTypes';
import {
  type ApprovalRightWithMemberName,
  type ApprovalRuleWithMemberNames,
  type ApprovalSchemeWithMemberNames,
} from '../../hooks/useApprovalSchemesQuery';

const convertApprover = (
  right: ApprovalRightWithMemberName,
  t: TGlobalFunctionTyped,
): ApprovalApproverExport => {
  switch (right.approverType) {
    case 'user':
      return { type: 'user', name: right.approverName || right.approverId };
    case 'accountOwner':
      return {
        type: 'role',
        role: 'accountOwner',
        name: t('teams.approvalFlows.accountOwner'),
      };
    case 'reportingManager':
      return {
        type: 'role',
        role: 'reportingManager',
        name: t('teams.approvalFlows.reportingManager'),
      };
    case 'costCenterOwner':
      return {
        type: 'role',
        role: 'costCenterOwner',
        name: t('teams.approvalFlows.costCenterOwner'),
      };
    default:
      throw new TypeGuardError(right, 'Unknown approver type');
  }
};

const formatMoneyAmount = (amount: ApprovalRuleAmount): string =>
  formatMoney(amount.value ?? 0, amount.currency);

const getRuleRange = (
  rule: ApprovalRuleWithMemberNames,
  ruleIndex: number,
  rules: ApprovalRuleWithMemberNames[],
): ApprovalRangeExport => {
  const zeroAmount = formatMoneyAmount({
    value: 0,
    currency: rule.from.currency,
  });
  const fromAmount = formatMoneyAmount(rule.from);
  const upToAmount = formatMoneyAmount(rule.upTo);

  if (approvalModels.isTheLastRule(rules, ruleIndex) && ruleIndex === 0) {
    return { type: 'noRange' };
  }
  if (approvalModels.isTheLastRule(rules, ruleIndex)) {
    return { type: 'from', from: fromAmount };
  }
  if (rule.from.value === 0 || rule.from.value === null) {
    return { type: 'upTo', from: zeroAmount, upTo: upToAmount };
  }
  return { type: 'fromUpTo', from: fromAmount, upTo: upToAmount };
};

const getRuleApprovers = (
  rule: ApprovalRuleWithMemberNames,
  t: TGlobalFunctionTyped,
) =>
  rule.steps.flatMap((step) =>
    step.rights.map((right) => convertApprover(right, t)),
  );

const getRule = (
  rule: ApprovalRuleWithMemberNames,
  ruleIndex: number,
  rules: ApprovalRuleWithMemberNames[],
  t: TGlobalFunctionTyped,
): ApprovalRuleExport => ({
  range: getRuleRange(rule, ruleIndex, rules),
  type: rule.steps.length > 1 ? 'allInOrder' : 'anyOf',
  approvers: getRuleApprovers(rule, t),
});

export const exportApprovalScheme = (
  index: number,
  approvalScheme: ApprovalSchemeWithMemberNames,
  t: TGlobalFunctionTyped,
): ApprovalSchemeExport => ({
  id: `${index}`,
  rules: approvalScheme.rules.map((rule, ruleIndex, rules) =>
    getRule(rule, ruleIndex, rules, t),
  ),
});
